// Hooks
import { useAppSelector, useAppDispatch } from '../../../app/hooks';
import useSessionAccess from '../../../hooks/useSessionAccess';

// Features
import { clearChatHistory } from '../../../features/chat/chatSlice';
import {
    selectSession,
    selectSessionWinners,
    clearSession,
} from '../../../features/session/sessionSlice';

// Components
import SessionScreen from '../../organisms/SessionScreen/SessionScreen';
import SessionIdle from '../../molecules/SessionIdle/SessionIdle';
import SessionWinners from '../../molecules/SessionWinners/SessionWinners';
import Leaderboard from '../../molecules/Leaderboard/Leaderboard';
import Button from '../../atoms/Button/Button';
import Lead from '../../atoms/Lead/Lead';

// Styles
import styles from '../../molecules/SessionIdle/SessionIdle.module.css';

// Utilities
import compareTimes from '../../../utils/date/compareTimes';

/**
 * @function SessionOver
 * Summary of previous game statistics
 *
 * @example
 * return (
 *   <SessionOver />
 * )
 */
function SessionOver(): JSX.Element {
    const dispatch = useAppDispatch();
    const session = useAppSelector(selectSession);
    const { gameWinners, accumulatedWinners } =
        useAppSelector(selectSessionWinners);
    const overallWinner = accumulatedWinners[0];

    if (session === null) return <></>;

    const startTime = new Date(`${session?.next_session_start_at}`);
    const timeNow = new Date();
    const timeDiff = compareTimes(startTime, timeNow);
    const sessionAccess = useSessionAccess(
        session,
        session.next_session_id,
        session.games.length > 1 ? 'Join Next Session' : 'Join Next Game',
        false,
    );

    function handleLeaveClick(event: any) {
        dispatch(clearSession());
        dispatch(clearChatHistory());
        event.preventDefault();
    }

    return (
        <SessionScreen>
            <SessionIdle
                heading={
                    session?.games !== undefined && session.games.length > 1
                        ? 'Session Over'
                        : 'Game Over'
                }
            >
                {session?.session.tournament === true ? (
                    <>
                        <Lead>
                            <b>{overallWinner.username}</b> wins with{' '}
                            <b>{overallWinner.points} PP</b>!
                        </Lead>
                        <Leaderboard
                            title="Session Winners"
                            winners={accumulatedWinners}
                        />
                    </>
                ) : (
                    <SessionWinners winners={gameWinners} />
                )}
                <footer className={styles.footer}>
                    {session?.state === 'closed' && (
                        <Lead>
                            The{' '}
                            {session.games.length > 1
                                ? `session is now closed`
                                : `game is now over`}
                            . Please return to the lobby to join{' '}
                            {session.games.length > 1
                                ? `a new session`
                                : `another game`}
                            .
                        </Lead>
                    )}
                    {session?.next_session_id !== undefined &&
                    timeDiff > 0 &&
                    timeDiff <= 3600 ? (
                        <Button
                            text={sessionAccess.text}
                            style="outline"
                            onClick={() => sessionAccess.onClick()}
                            disabled={sessionAccess.disabled}
                        />
                    ) : (
                        <></>
                    )}
                    <Button
                        text="Return to Lobby"
                        onClick={(e) => handleLeaveClick(e)}
                    />
                </footer>
            </SessionIdle>
        </SessionScreen>
    );
}

// Exports
export default SessionOver;
